import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import { validateGamePassword, PASSWORD_MAX_LEN, PASSWORD_MIN_LEN, PASSWORD_HINT } from '../lib/passwordRules';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

function RequestForm() {
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el correo. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="register-card" style={{ textAlign: 'center' }}>
        <CheckCircle size={52} className="success-icon" style={{ margin: '0 auto 16px' }} />
        <h2 className="register-title">Correo enviado</h2>
        <p className="register-subtitle" style={{ marginTop: 8 }}>
          Si existe una cuenta con ese correo, recibirás un enlace para restablecer tu contraseña en breve.
        </p>
        <Link to="/login" className="terms-link" style={{ display: 'block', marginTop: 24, fontSize: 13 }}>
          Volver al inicio de sesión
        </Link>
      </div>
    );
  }

  return (
    <div className="register-card">
      <div className="register-header">
        <p className="register-eyebrow">FROSTBOUND</p>
        <h1 className="register-title">Recuperar Contraseña</h1>
        <p className="register-subtitle">
          Introduce tu correo y te enviaremos un enlace para restablecer tu contraseña.
        </p>
      </div>

      {error && (
        <div className="register-error">
          <AlertCircle size={15} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="register-form">
        <div className="form-field">
          <label htmlFor="recup-email" className="form-label">CORREO ELECTRÓNICO</label>
          <div className="input-wrap">
            <Mail size={15} className="input-icon" />
            <input
              id="recup-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="tu@correo.com"
              className="form-input"
              required
              autoComplete="email"
            />
          </div>
        </div>

        <button
          type="submit"
          className={`register-btn ${loading ? 'register-btn--loading' : ''}`}
          disabled={loading}
          style={{ marginTop: 8 }}
        >
          {loading ? 'ENVIANDO...' : 'ENVIAR ENLACE'}
        </button>
      </form>

      <p className="register-login-link" style={{ marginTop: 16 }}>
        <Link to="/login" className="terms-link">Volver al inicio de sesión</Link>
      </p>
    </div>
  );
}

function ResetForm({ token }) {
  const [form, setForm]         = useState({ password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Las contraseñas no coinciden.'); return; }
    const passwordError = validateGamePassword(form.password);
    if (passwordError) { setError(passwordError); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: form.password,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'El enlace no es válido o ha caducado.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="register-card" style={{ textAlign: 'center' }}>
        <CheckCircle size={52} className="success-icon" style={{ margin: '0 auto 16px' }} />
        <h2 className="register-title">Contraseña actualizada</h2>
        <p className="register-subtitle" style={{ marginTop: 8 }}>
          Tu contraseña ha sido restablecida correctamente.
        </p>
        <Link to="/login" className="register-btn" style={{ display: 'inline-block', marginTop: 24, textAlign: 'center' }}>
          Iniciar Sesión
        </Link>
      </div>
    );
  }

  return (
    <div className="register-card">
      <div className="register-header">
        <p className="register-eyebrow">FROSTBOUND</p>
        <h1 className="register-title">Nueva Contraseña</h1>
        <p className="register-subtitle">Introduce tu nueva contraseña para recuperar el acceso.</p>
      </div>

      {error && (
        <div className="register-error">
          <AlertCircle size={15} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="register-form">
        <div className="form-field">
          <label htmlFor="recup-password" className="form-label">NUEVA CONTRASEÑA</label>
          <div className="input-wrap">
            <Lock size={15} className="input-icon" />
            <input
              id="recup-password"
              type={showPass ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => { setForm(prev => ({ ...prev, password: e.target.value })); setError(''); }}
              placeholder="Mínimo 8 caracteres"
              className="form-input"
              required
              minLength={PASSWORD_MIN_LEN}
              maxLength={PASSWORD_MAX_LEN}
              title={PASSWORD_HINT}
              autoComplete="new-password"
            />
            <button type="button" className="pass-toggle" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
              {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="recup-confirm" className="form-label">CONFIRMAR CONTRASEÑA</label>
          <div className="input-wrap">
            <Lock size={15} className="input-icon" />
            <input
              id="recup-confirm"
              type={showPass ? 'text' : 'password'}
              value={form.confirm}
              onChange={(e) => { setForm(prev => ({ ...prev, confirm: e.target.value })); setError(''); }}
              placeholder="Repite la contraseña"
              className="form-input"
              required
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          className={`register-btn ${loading ? 'register-btn--loading' : ''}`}
          disabled={loading}
          style={{ marginTop: 8 }}
        >
          {loading ? 'GUARDANDO...' : 'CAMBIAR CONTRASENA'}
        </button>
      </form>
    </div>
  );
}

export default function RecuperarContrasenaPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <PageWrapper scrollable>
      <SEOMeta title="Recuperar contraseña" noIndex />
      <Navbar />
      <main className="register-main anim-fade-up" style={{ animationDelay: '0.2s' }}>
        {token ? <ResetForm token={token} /> : <RequestForm />}
      </main>
      <Footer />
    </PageWrapper>
  );
}
